<script lang="ts" setup>
// Vue
import { computed, InputHTMLAttributes, toRef } from 'vue';

import { useBugsnag } from '@/analytics/bugsnag/composables/useBugsnag';
// Classes
import Element from '@/elements/element/classes/Element';
import { useI18n } from '@/i18n/useI18n';
import MathTools from '@/utils/classes/MathTools';

// Constants
const INPUT_CONFIG = {
	MAX: 1,
	MIN: 0,
	STEP: 0.01,
	TYPE: 'range',
};

// Props
const props = defineProps<{
	element: Element;
	isMalfunction?: boolean; // ?
	opacity: number;
}>();
const element = toRef(props, 'element');
// Computeds
const opacityPercentage = computed(() => props.opacity * 100);

const opacityPercentageLabel = computed(() => Math.round(opacityPercentage.value)); // ?

// Methods
const { breadScrumbWithDebounce } = useBugsnag(element);
const { trans } = useI18n();
const onInput = (e: Event) => {
	const value = MathTools.clamp((e.target as InputHTMLAttributes).value, INPUT_CONFIG.MIN, INPUT_CONFIG.MAX);
	props.element.setOpacity(value);
	breadScrumbWithDebounce('opacity');
};
</script>

<template>
	<div>
		<h4 class="mb-3 text-sm font-bold uppercase text-gray-100 opacity-75 mockup:text-fp-gray-800">
			{{ trans('Opacity') }}
		</h4>
		<div class="mb-6 flex items-center" :class="{ malfunction: isMalfunction }" data-testid="opacity-input">
			<div class="relative flex w-full items-center">
				<input
					class="input-range h-[2px] w-full appearance-none rounded-full bg-gray-900 focus:outline-none mockup:bg-fp-gray-200"
					:max="INPUT_CONFIG.MAX"
					:min="INPUT_CONFIG.MIN"
					:step="INPUT_CONFIG.STEP"
					:type="INPUT_CONFIG.TYPE"
					:value="opacity"
					@input="onInput"
				/>

				<span
					class="pointer-events-none absolute left-0 top-0 h-[2px] rounded-full"
					data-testid="opacityInput"
					:class="isMalfunction ? 'opacity-malfunction-bar' : 'bg-blue-500 slidesgo:bg-purple-400'"
					:style="{
						width: `${opacityPercentage}%`,
					}"
				/>
			</div>

			<span class="ml-2 w-8 text-xs text-gray-100 mockup:text-fp-gray-700">{{ opacityPercentageLabel }}%</span>
		</div>
	</div>
</template>

<style lang="sass">
.input-range::-webkit-slider-thumb
	@apply w-3 h-3 bg-blue-500 slidesgo:bg-purple-400 appearance-none rounded-full cursor-ew-resize
</style>
